import presentationUI from 'lib/presentation-ui';
import { zoomUI } from 'lib/zoom-ui';
import { pui } from 'lib/perspectives-ui';
import { ctui } from 'lib/creation-tools-ui';
import { nui } from 'lib/nodes-ui';
import { KEY_CODES, ALLOWED_IMAGE_FILE_TYPES } from 'lib/constants';
import EventTracker from 'lib/event-tracking';
import CopyPaste from 'lib/copy-paste';
import { delayHandler } from 'lib/delay-handler';
import { mui } from 'lib/menu-ui';
import { dragdrop } from 'lib/drag-drop';
import { searchUI } from 'lib/search-ui';
import { canPlayVideo } from 'lib/util';

const { SPACE, CTRL, SHIFT } = KEY_CODES;
const config = window.config;

export const setupGlobalEventListeners = canvas => {
  canvas.space = false;

  searchUI.searchKeyOverride(document.body, true);

  const invisibleLayer = document.createElement('div');
  invisibleLayer.style.width = '100%';
  invisibleLayer.style.height = '100%';
  invisibleLayer.style.opacity = 0;
  invisibleLayer.classList.add('panzoomable', 'spacebar-zoom-layer');

  document.addEventListener('keyup', e => {
    if (e.keyCode === SPACE) {
      canvas.space = false;
      if (invisibleLayer.parentNode && invisibleLayer.parentNode == canvas.refs.canvas) {
        canvas.refs.canvas.removeChild(invisibleLayer);
      }
    }
    if (!e.shiftKey) {
      canvas.shiftKey = false;
      if (canvas.refs.canvas.classList.contains('shift-key-down')) {
        canvas.refs.canvas.classList.remove('shift-key-down');
      }
    }

    if (e.keyCode === SHIFT && canvas.activeToolName !== 'pointer') {
      ctui.setActiveTool('pointer');
    }


    //91 and 93 are left and right meta for some reason
    if (e.keyCode == CTRL || e.keyCode == 91 || e.keyCode == 93 ) {
      canvas.inTheMiddleOfMetaDrag = false;
    };
  });

  document.addEventListener('keydown', e => {
    if (e.keyCode === SPACE && !canvas.space) {
      canvas.space = true;
      canvas.refs.canvas.appendChild(invisibleLayer);
    }

    if (e.shiftKey) {
      canvas.shiftKey = true;
      if (!canvas.refs.canvas.classList.contains('shift-key-down')) {
        canvas.refs.canvas.classList.add('shift-key-down');
      }
    }
    // handle copy and paste a node for Firefox and Safari
    // if ((e.ctrlKey || e.metaKey) && e.which == 67 && (canvas.isFirefox || canvas.isSafari)) {
    //   canvas.copyNode(e);
    // } else if ((e.ctrlKey || e.metaKey) && e.which == 86 && (canvas.isFirefox || canvas.isSafari)
    //   && canvas.shiftKey && canvas.getSelectedElementIds().length == 1 ) {
    //   // this code path only pastes nodes
    //   // anything requiring clipboard data needs to go through the paste handler
    //   canvas.handlePaste(e);
    if (e.metaKey && e.altKey && e.keyCode === 187) {
      // alt + command + plus to zoom in (mousetrap doesn't handle this right)
      e.preventDefault();
      $(canvas.refs.canvas).panzoom("zoom", zoomUI.roundScale(zoomUI.currentScale + zoomUI.increment));
    }
  });

  document.addEventListener('dragover', e => {
    canvas.mouseX = e.clientX;
    canvas.mouseY = e.clientY;
    e.preventDefault();
  });

  window.addEventListener('drop', e => {
    e.preventDefault();
    const { scale, tx, ty } = canvas.getMatrix();
    canvas.lastNodeCreatedWithEnterKey = null;

    let x = canvas.mouseX;
    let y = canvas.mouseY;
    if (x === 0 && y === 0) {
      let x = window.innerWidth / 2;
      let y = window.innerHeight / 2;
    }

    const scaledX = (x - tx - (64 * scale)) / scale;
    const scaledY = (y - ty - (24 * scale)) / scale;

    const dt = e.dataTransfer;
    let file;
    if (dt.items) {
      for (var i=0; i < dt.items.length; i++) {
        if (dt.items[i].kind == "file") {
          file = dt.items[i].getAsFile();
        }
      }
    } else {
      for (var i=0; i < dt.files.length; i++) {
        if (dt.files[i].type.indexOf("image") !== -1) {
          file = dt.files[i];
        }
      }
    }
    if (!file) return;

    const cardless = (ALLOWED_IMAGE_FILE_TYPES.includes(file.type) || canPlayVideo(file.type));

    canvas.createNewNode({ x: scaledX, y: scaledY, fromShortcut: true, fileEvent: e, cardless });
    canvas.appFooterTag.updateStyle();
  });


  const gallery = canvas.tags['gallery'];

  document.addEventListener('mousemove', e => {
    const galleryHotZones = [
      // whole left side
      {
        top: 0,
        bottom: window.innerHeight,
        left: 0,
        right: 96,
      },
      // whole right side
      {
        top: 0,
        bottom: window.innerHeight,
        left: window.innerWidth - 96,
        right: window.innerWidth,
      },
      // whole top
      {
        top: 0,
        bottom: 80,
        left: 0,
        right: window.innerWidth,
      },
    ];

    const mouseRect = {
      top: e.clientY,
      bottom: e.clientY,
      left: e.clientX,
      right: e.clientX,
    };

    if (gallery.isVisible) {
      gallery.edgeHover = false;

      for (const zone of galleryHotZones) {
        if (dragdrop.hit(mouseRect, zone)) {
          gallery.edgeHover = true;
        }
      }

      if (gallery.edgeHover) {
        delayHandler.clearDelayFor('gallery-edge-hover');
        gallery.displayChrome();
      } else if (!delayHandler.isDelayed('galleryEdgeHover', 'gallery')) {
        delayHandler.initializeDelay({
          action: 'gallery-edge-hover',
          id: 'gallery',
          delay: 600,
          callback: gallery.hideChrome,
        });
      }
    } else if (canvas.zenMode) {
      const xBuffer = canvas.toolsSidebarTag.isOpen ? 350 : 60;
      const yBuffer = 60;

      const hotZones = [
        //whole left side
        {
          top: 0,
          bottom: window.innerHeight,
          left: 0,
          right: xBuffer,
        },
        //bottom right corner (zoom tools)
        {
          top: window.innerHeight - yBuffer,
          bottom: window.innerHeight,
          left: window.innerWidth - 200,
          right: window.innerWidth,
        },
        //whole top (-80 to account for moving towards turn off focus button)
        {
          top: 0,
          bottom: yBuffer,
          left: 0,
          right: window.innerWidth - 80,
        },
      ];

      canvas.zenModeEdgeHover = false;
      for (const zone of hotZones) {
        if (dragdrop.hit(mouseRect, zone)) {
          canvas.zenModeEdgeHover = true;
        }
      }

      if (!canvas.zenModeEdgeHover) {
        delayHandler.clearDelayFor('zen-mode-edge-hover');
        if (canvas.tempUnZenMode && !mui.chromeMenusAreOpen()) {
          canvas.setZenMode();
          canvas.tempUnZenMode = false;
        }
      } else if (canvas.zenModeEdgeHover) {
        if (!delayHandler.isDelayed('zenModeEdgeHover', 'zen')) {
          delayHandler.initializeDelay({
            action: 'zen-mode-edge-hover',
            id: 'zen',
            delay: 600,
            callback: () => {
              if (canvas.zenModeEdgeHover && !canvas.bulkActionsMenuTag.mouseEntered) {
                canvas.tempUnZenMode = true;
                canvas.unSetZenMode();
              }
            },
          });
        }
      }
    }


    canvas.mouseX = e.clientX;
    canvas.mouseY = e.clientY;
    canvas.mouseMovedRecently = true;
    const mouseMovedRecentlyDuration = 2000;

    if (presentationUI.isPresenting) {
      canvas.presentationControlsTag.show();
      canvas.waypointsTabTag.showCursor();
    }
    if (canvas.mouseMovedRecentlyTimeout) {
      clearTimeout(canvas.mouseMovedRecentlyTimeout);
    }
    canvas.mouseMovedRecentlyTimeout = setTimeout(
      () => {
        canvas.mouseMovedRecently = false;
        if (!canvas.presentationControlsTag.hovered) {
          canvas.presentationControlsTag.hide();
        }
      },
      mouseMovedRecentlyDuration
    );
  });

  document.body.addEventListener('copy', e => {
    // if text field is in focus but no text is selected, copy the node
    canvas.copyNode(e);

    EventTracker.track({
      category: 'Shortcut',
      action: 'Copy-Selection',
      label: 'Selection',
    });
  }, true);


  document.body.addEventListener('cut', canvas.handleCut, true);


  // regular paste to top level of canvas
  document.body.addEventListener('paste', e => {
    canvas.handlePaste(e);
  }, true);

  document.body.addEventListener('click', ({ target, clientX, clientY }) => {
    if (!canvas.loadedTemplate || target !== canvas.refs.pannable) return;

    const { loadedComponentId } = canvas;

    const data = canvas.loadedTemplate;
    const position = {
      x: clientX,
      y: clientY,
    };

    const { nodeOldtoNewIds } = CopyPaste.pasteData({ data, canvas, position });

    if (Object.keys(nodeOldtoNewIds).length) {
      const topNode = canvas.store.getNode(Object.values(nodeOldtoNewIds)[0]).getTopLevelParent();
      const topNodeTag = canvas.nodeTags[topNode.id];
      topNodeTag.focusTitle(true);

      const messages = [];

      const { prevNodePositions, nodePositions } = nui.fixOverlaps({ nodeTag: topNodeTag });
      if (nodePositions.length) {
        messages.push(canvas.operation.messages.updateNodePositions({
          prevNodePositions,
          nodePositions,
          prevLinkAngles: [],
          linkAngles: [],
        }));
      }

      canvas.operation.batchOperation({
        messages,
        onFailure: (err) => {
          console.log(`batchOperation failure: ${err}`);
        }
      });
    }

    canvas.creationTools.clearSelection();
  }, true);

  document.body.addEventListener('mousedown', e => {
    if (e.target.isContentEditable) {
      e.stopImmediatePropagation();
    }
  }, true);
  document.body.addEventListener('touchstart', e => {
    if (e.target.isContentEditable) {
      e.stopImmediatePropagation();
    }
  }, true);
  document.body.addEventListener('pointerdown', e => {
    if (e.target.isContentEditable) {
      e.stopImmediatePropagation();
    }
  }, true);

  document.body.addEventListener('dblclick', e => {
    if (e.target.getAttribute('ref') == 'menuTool') {
      e.stopPropagation();
    }
  }, true);


};



// WEBPACK FOOTER //
// ./starling/src/lib/global-event-listeners.js